<template>
  <div class="highway-filter-panel">
    <div class="filter-panel-body">
      <!-- Selected States Summary -->
      <div class="selected-summary">
        <span v-if="localSelectedStates.length === 0" class="summary-text">
          No states selected
        </span>
        <span v-else class="summary-text">
          {{ localSelectedStates.length }} state{{ localSelectedStates.length !== 1 ? 's' : '' }} selected
        </span>
      </div>
      
      <!-- Collapsible State Selector -->
      <div class="state-selector-wrapper">
        <ThemedButton
          variant="outline"
          @click="statesExpanded = !statesExpanded"
          type="button"
          class="state-selector-trigger w-full"
        >
          <span>{{ statesExpanded ? 'Hide' : 'Select' }} States</span>
          <ThemeIcon 
            icon="chevron-down" 
            type="icons" 
            :classes="statesExpanded ? 'dropdown-arrow open' : 'dropdown-arrow'" 
          />
        </ThemedButton>
        
        <div v-if="statesExpanded" class="state-selector-content">
          <!-- Search Box -->
          <div class="search-box">
            <ThemedInput
              v-model="searchQuery"
              type="search"
              placeholder="Search states..."
            />
          </div>
          
          <!-- State Grid -->
          <div class="state-grid">
            <label
              v-for="state in filteredStates"
              :key="state.code"
              class="state-checkbox"
            >
              <ThemedCheckbox
                :value="localSelectedStates.includes(state.code)"
                @input="toggleState(state.code)"
                :label="state.name"
              />
            </label>
          </div>
          
          <div v-if="filteredStates.length === 0" class="no-results">
            No states found
          </div>
        </div>
      </div>
      
      <!-- Action Buttons -->
      <div class="filter-panel-footer">
        <ThemedButton
          variant="outline"
          @click="clearSelection"
          :disabled="localSelectedStates.length === 0"
        >
          Clear
        </ThemedButton>
        <ThemedButton
          variant="default"
          @click="loadHighways"
          :disabled="localSelectedStates.length === 0"
        >
          Load Highways
        </ThemedButton>
      </div>
    </div>
  </div>
</template>

<script>
import ThemeIcon from '../ThemeIcon.vue';
import ThemedInput from '../ThemedInput.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedCheckbox from '../ThemedCheckbox.vue';

export default {
  name: 'HighwayFilterPanel',

  components: {
    ThemeIcon,
    ThemedInput,
    ThemedButton,
    ThemedCheckbox
  },
  
  props: {
    show: {
      type: Boolean,
      default: false
    },
    selectedStates: {
      type: Array,
      default: () => []
    },
    availableStates: {
      type: Array,
      default: () => []
    },
    onLoadHighways: {
      type: Function,
      default: null
    }
  },
  
  data() {
    return {
      localSelectedStates: [],
      searchQuery: '',
      statesExpanded: false
    };
  },
  
  computed: {
    sortedStates() {
      return [...this.availableStates].sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
    },
    
    filteredStates() {
      if (!this.searchQuery) {
        return this.sortedStates;
      }
      
      const query = this.searchQuery.toLowerCase();
      return this.sortedStates.filter(state => 
        state.name.toLowerCase().includes(query) || 
        state.code.toLowerCase().includes(query)
      );
    }
  },
  
  watch: {
    selectedStates: {
      handler(newStates) {
        // Only sync if the parent's value actually differs from local
        // (avoids resetting user selections when parent re-renders)
        const newSorted = [...newStates].sort().join(',');
        const localSorted = [...this.localSelectedStates].sort().join(',');
        if (newSorted !== localSorted) {
          this.localSelectedStates = [...newStates];
        }
      },
      immediate: true
    }
  },
  
  mounted() {},
  
  methods: {
    toggleState(stateCode) {
      const index = this.localSelectedStates.indexOf(stateCode);
      if (index > -1) {
        this.localSelectedStates.splice(index, 1);
      } else {
        this.localSelectedStates.push(stateCode);
      }
    },
    
    clearSelection() {
      this.localSelectedStates = [];
    },
    
    loadHighways() {
      if (this.localSelectedStates.length > 0) {
        if (this.onLoadHighways && typeof this.onLoadHighways === 'function') {
          this.onLoadHighways(this.localSelectedStates);
        }
        this.$emit('load-highways', this.localSelectedStates);
      }
    }
  }
};
</script>
